/**
 * Clinic entry slug (`/c/<slug>`) — ZOY-608 contract, ZOY-614 consumer.
 *
 * The slug is an ENTRY ALIAS and nothing else: it preselects a clinic and
 * brands the sign-in card. Authority stays exactly what it is everywhere
 * else — the caller's live membership, selected per request through the
 * server-validated `x-clinic-id` (ADR-013). Resolving a slug grants nothing:
 * a guarded request for a clinic the caller is not a member of still answers
 * `403 CLINIC_CONTEXT_INVALID`.
 *
 * The rules and the reserved list below are mirrored VERBATIM from
 * cms-backend `src/clinic/slug.ts` so `/c/<reserved>` renders not-found
 * without a network call. Unknown, malformed and reserved values are
 * deliberately indistinguishable on the wire (all `404
 * CLINIC_SLUG_NOT_FOUND`), and this client keeps them indistinguishable too.
 */

import { apiRequest } from '../api/http';

export const SLUG_MIN_LENGTH = 2;
export const SLUG_MAX_LENGTH = 63;

/** Lowercase `a-z0-9`, single `-` separators, no leading/trailing/double `-`. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Mirrored from cms-backend `RESERVED_SLUGS` (ZOY-608). */
export const RESERVED_SLUGS: readonly string[] = [
  'c',
  'signin',
  'signout',
  'login',
  'logout',
  'forgot',
  'reset',
  'today',
  'account',
  'calendar',
  'patients',
  'payments',
  'sessions',
  'bookings',
  'packages',
  'assessments',
  'intake',
  'reports',
  'clinic-setup',
  'setup',
  'settings',
  'staff',
  'hours',
  'rooms',
  'services',
  'catalog',
  'catalogs',
  'admin',
  'platform',
  'api',
  'v1',
  'v2',
  'public',
  'health',
  'docs',
  'docs-json',
  'static',
  'assets',
  'config',
  'config.json',
  'zoyaar',
  'clinic',
  'clinics',
  'www',
  'app',
  'auth',
  'me',
  'null',
  'undefined',
];

const RESERVED = new Set(RESERVED_SLUGS);

/** Shape + reserved word. Case is NOT folded: the canonical form is lowercase. */
export function isValidSlug(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= SLUG_MIN_LENGTH &&
    value.length <= SLUG_MAX_LENGTH &&
    SLUG_PATTERN.test(value) &&
    !RESERVED.has(value)
  );
}

/** What the anonymous resolver answers — display and preselection input only. */
export interface ClinicEntry {
  clinicId: string;
  slug: string;
  displayName: string | null;
}

export const ClinicEntryErrorCode = {
  SlugNotFound: 'CLINIC_SLUG_NOT_FOUND',
} as const;

/**
 * Anonymous resolution (no token, no clinic header). Unknown / malformed /
 * reserved all answer `404 CLINIC_SLUG_NOT_FOUND`.
 */
export const resolveClinicSlug = (slug: string) =>
  apiRequest<ClinicEntry>({
    base: 'clinic',
    path: `/v1/public/clinics/by-slug/${encodeURIComponent(slug)}`,
    auth: false,
    clinicHeader: false,
  });

/** The shareable entry URL of a clinic, on whatever origin this app is served from. */
export function entryUrlFor(slug: string, origin?: string): string {
  const base = origin ?? (typeof window === 'undefined' ? '' : window.location.origin);
  return `${base}/c/${slug}`;
}
